import { useState } from 'react';
import { signInWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { doc, deleteDoc, collection, getDocs, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../config/firebase';

const cancelSubscriptionFn = httpsCallable(getFunctions(), 'cancelSubscription');

export default function DeleteAccount() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleDelete(e) {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Sign in to verify identity
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const uid = cred.user.uid;

      // Cancel Stripe subscription if active
      try { await cancelSubscriptionFn(); } catch (e) {}

      // Delete all user data
      const batch = writeBatch(db);

      const tripsSnap = await getDocs(collection(db, 'users', uid, 'trips'));
      tripsSnap.docs.forEach((d) => batch.delete(d.ref));

      const expensesSnap = await getDocs(collection(db, 'users', uid, 'expenses'));
      expensesSnap.docs.forEach((d) => batch.delete(d.ref));

      batch.delete(doc(db, 'users', uid));
      await batch.commit();

      // Delete auth account
      await deleteUser(cred.user);

      setSuccess(true);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password.');
      } else {
        setError('Failed to delete account. Please try again or contact support.');
      }
    }
    setLoading(false);
  }

  if (success) {
    return (
      <div style={{ maxWidth: 480, margin: '60px auto', padding: '0 20px', fontFamily: 'Inter, sans-serif' }}>
        <h1 style={{ fontSize: 24, marginBottom: 16 }}>Account Deleted</h1>
        <p style={{ color: '#64748B', lineHeight: 1.6 }}>
          Your MileDeduct account and all associated data (trips, expenses, vehicle info, and profile) have been permanently deleted.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '60px auto', padding: '0 20px', fontFamily: 'Inter, sans-serif' }}>
      <h1 style={{ fontSize: 24, marginBottom: 8 }}>Delete Your MileDeduct Account</h1>
      <p style={{ color: '#64748B', lineHeight: 1.6, marginBottom: 24 }}>
        This will permanently delete your account and all associated data, including:
      </p>
      <ul style={{ color: '#64748B', lineHeight: 1.8, marginBottom: 24, paddingLeft: 20 }}>
        <li>All trip records and GPS data</li>
        <li>All expense records</li>
        <li>Vehicle information</li>
        <li>Profile and account data</li>
      </ul>
      <p style={{ color: '#EF4444', fontWeight: 600, marginBottom: 24 }}>
        This action cannot be undone.
      </p>

      {error && <p style={{ color: '#EF4444', marginBottom: 16 }}>{error}</p>}

      <form onSubmit={handleDelete}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%', padding: '12px', background: '#EF4444', color: 'white',
            border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Deleting...' : 'Delete My Account'}
        </button>
      </form>
    </div>
  );
}
